// src/theme/design-system.ts
import { Type } from "@sinclair/typebox";
var TYPE_ROLES = [
  "eyebrow",
  "display",
  "stat",
  "quote",
  "label",
  "footer",
  "tableHeader",
  "tableCell",
  "chartLabel",
  "tracker",
  "source"
];
var TypeRoleNameSchema = Type.Union(
  TYPE_ROLES.map((name) => Type.Literal(name))
);
var CANVASES = ["a4", "letter", "wide169", "standard43"];
var TextCaseSchema = Type.Union([
  Type.Literal("none"),
  Type.Literal("upper"),
  Type.Literal("smallCaps")
]);
var ColorTokenSchema = Type.String({
  pattern: "^(#?[0-9A-Fa-f]{6}|[a-zA-Z][a-zA-Z0-9]*)$",
  description: "Six-digit hex or a theme color/palette token. References must resolve without cycles."
});
var PaletteSchema = Type.Object(
  {
    rule: Type.Optional(ColorTokenSchema),
    textMuted: Type.Optional(ColorTokenSchema),
    onPrimary: Type.Optional(ColorTokenSchema),
    surfaceInverse: Type.Optional(ColorTokenSchema),
    positive: Type.Optional(ColorTokenSchema),
    negative: Type.Optional(ColorTokenSchema),
    chart: Type.Optional(
      Type.Array(ColorTokenSchema, { minItems: 1, maxItems: 12 })
    )
  },
  { additionalProperties: false }
);
var TypeRoleSchema = Type.Object(
  {
    face: Type.Optional(
      Type.Union([
        Type.Literal("heading"),
        Type.Literal("body"),
        Type.Literal("mono"),
        Type.Literal("light")
      ])
    ),
    weight: Type.Optional(Type.Integer({ minimum: 100, maximum: 900 })),
    size: Type.Optional(
      Type.Number({
        minimum: 5,
        maximum: 200,
        description: "Explicit size in points; takes precedence over the canvas scale."
      })
    ),
    lineHeight: Type.Optional(Type.Number({ minimum: 0.5, maximum: 3 })),
    tracking: Type.Optional(
      Type.Number({ description: "Tracking in hundredths of an em." })
    ),
    case: Type.Optional(TextCaseSchema),
    color: Type.Optional(ColorTokenSchema),
    spaceBefore: Type.Optional(Type.Number({ minimum: 0 })),
    spaceAfter: Type.Optional(Type.Number({ minimum: 0 }))
  },
  { additionalProperties: false }
);
var TypeRolesSchema = Type.Object(
  {
    eyebrow: Type.Optional(TypeRoleSchema),
    display: Type.Optional(TypeRoleSchema),
    stat: Type.Optional(TypeRoleSchema),
    quote: Type.Optional(TypeRoleSchema),
    label: Type.Optional(TypeRoleSchema),
    footer: Type.Optional(TypeRoleSchema),
    tableHeader: Type.Optional(TypeRoleSchema),
    tableCell: Type.Optional(TypeRoleSchema),
    chartLabel: Type.Optional(TypeRoleSchema),
    tracker: Type.Optional(TypeRoleSchema),
    source: Type.Optional(TypeRoleSchema)
  },
  { additionalProperties: false }
);
var ScaleSchema = Type.Object(
  {
    base: Type.Number({ minimum: 5, maximum: 200 }),
    ratio: Type.Optional(Type.Number({ minimum: 1, maximum: 2 })),
    baselinePt: Type.Optional(
      Type.Number({ exclusiveMinimum: 0, maximum: 24 })
    )
  },
  { additionalProperties: false }
);
var TypographySchema = Type.Object(
  {
    roles: Type.Optional(TypeRolesSchema),
    scale: Type.Optional(
      Type.Object(
        {
          a4: Type.Optional(ScaleSchema),
          letter: Type.Optional(ScaleSchema),
          wide169: Type.Optional(ScaleSchema),
          standard43: Type.Optional(ScaleSchema)
        },
        { additionalProperties: false }
      )
    )
  },
  { additionalProperties: false }
);
var CanvasSpacingSchema = Type.Object(
  {
    safeAreaIn: Type.Optional(Type.Number({ minimum: 0 })),
    gutterIn: Type.Optional(Type.Number({ minimum: 0 })),
    columns: Type.Optional(Type.Integer({ minimum: 1, maximum: 100 })),
    rows: Type.Optional(Type.Integer({ minimum: 1, maximum: 100 }))
  },
  { additionalProperties: false }
);
var DesignSpacingSchema = Type.Object(
  {
    basePt: Type.Optional(Type.Number({ exclusiveMinimum: 0 })),
    blockGap: Type.Optional(
      Type.Object(
        {
          tight: Type.Optional(Type.Number({ minimum: 0 })),
          normal: Type.Optional(Type.Number({ minimum: 0 })),
          loose: Type.Optional(Type.Number({ minimum: 0 }))
        },
        { additionalProperties: false }
      )
    ),
    canvas: Type.Optional(
      Type.Object(
        {
          a4: Type.Optional(CanvasSpacingSchema),
          letter: Type.Optional(CanvasSpacingSchema),
          wide169: Type.Optional(CanvasSpacingSchema),
          standard43: Type.Optional(CanvasSpacingSchema)
        },
        { additionalProperties: false }
      )
    )
  },
  { additionalProperties: false }
);
var RecipeSchema = Type.Object(
  {
    type: Type.Optional(TypeRoleNameSchema),
    color: Type.Optional(ColorTokenSchema),
    fill: Type.Optional(ColorTokenSchema),
    rule: Type.Optional(
      Type.Object(
        {
          weightPt: Type.Optional(Type.Number({ minimum: 0 })),
          color: Type.Optional(ColorTokenSchema)
        },
        { additionalProperties: false }
      )
    ),
    padPt: Type.Optional(Type.Number({ minimum: 0 })),
    alignment: Type.Optional(
      Type.Union([
        Type.Literal("left"),
        Type.Literal("center"),
        Type.Literal("right")
      ])
    )
  },
  { additionalProperties: false }
);
var ChromeSchema = Type.Object(
  {
    runningHead: Type.Optional(RecipeSchema),
    tracker: Type.Optional(RecipeSchema),
    actionTitle: Type.Optional(RecipeSchema),
    keyTakeaways: Type.Optional(RecipeSchema),
    sourceLine: Type.Optional(RecipeSchema),
    confidentialFooter: Type.Optional(RecipeSchema),
    logoSlot: Type.Optional(RecipeSchema),
    cover: Type.Optional(RecipeSchema)
  },
  {
    additionalProperties: false,
    description: "Visual recipes; consumers land in #361. No automatic content or presence requirements."
  }
);
var MotifSchema = Type.Object(
  {
    kind: Type.Union([
      Type.Literal("none"),
      Type.Literal("rule"),
      Type.Literal("corner"),
      Type.Literal("band")
    ]),
    color: Type.Optional(ColorTokenSchema),
    weightPt: Type.Optional(Type.Number({ minimum: 0 })),
    placement: Type.Optional(
      Type.Union([
        Type.Literal("top"),
        Type.Literal("bottom"),
        Type.Literal("left"),
        Type.Literal("right"),
        Type.Literal("topLeft"),
        Type.Literal("topRight"),
        Type.Literal("bottomLeft"),
        Type.Literal("bottomRight")
      ])
    )
  },
  {
    additionalProperties: false,
    description: "At most one motif. Rendering consumers land in #361."
  }
);
var DesignSystemProperties = {
  palette: Type.Optional(PaletteSchema),
  typography: Type.Optional(TypographySchema),
  spacing: Type.Optional(DesignSpacingSchema),
  chrome: Type.Optional(ChromeSchema),
  motif: Type.Optional(MotifSchema)
};
var DesignSystemSchema = Type.Object(DesignSystemProperties, {
  additionalProperties: false
});
function designCanvas(format, size) {
  if (format === "docx") return size === "LETTER" ? "letter" : "a4";
  const ratio = typeof size === "object" ? size.width / size.height : 4 / 3;
  return Math.abs(ratio - 16 / 9) < Math.abs(ratio - 4 / 3) ? "wide169" : "standard43";
}
var ROLE_SCALE_STEPS = {
  display: 4,
  stat: 3,
  quote: 1,
  tableHeader: 0,
  tableCell: 0,
  label: -1,
  eyebrow: -1,
  chartLabel: -1,
  tracker: -1,
  footer: -2,
  source: -2
};
function scaledSize(scale, step) {
  if (step === 0) return scale.base;
  const baseline = scale.baselinePt ?? 4;
  const exact = scale.base * (scale.ratio ?? 1.25) ** step;
  return Math.max(5, Math.min(200, Math.round(exact / baseline) * baseline));
}
var SCALE_STEP_RANGE = { from: -2, to: 5 };
function typeScaleSizes(scale) {
  const sizes = /* @__PURE__ */ new Set();
  for (let step = SCALE_STEP_RANGE.from; step <= SCALE_STEP_RANGE.to; step += 1) {
    sizes.add(scaledSize(scale, step));
  }
  return [...sizes].sort((a, b) => a - b);
}
function paletteScalars(palette) {
  const scalars = {};
  for (const [key, value] of Object.entries(palette ?? {})) {
    if (typeof value === "string") scalars[key] = value;
  }
  return scalars;
}
function capsFormatting(textCase) {
  return textCase === "upper" ? { allCaps: true } : { smallCaps: textCase === "smallCaps" };
}
function resolveMotif(motif) {
  return motif && motif.kind !== "none" ? motif : void 0;
}
function resolveTypeRoles(system, canvas, base) {
  const scale = system.typography?.scale?.[canvas];
  const roles = {};
  for (const name of TYPE_ROLES) {
    const role = system.typography?.roles?.[name];
    if (!role) continue;
    roles[name] = {
      ...role,
      size: role.size ?? (scale ? scaledSize(scale, ROLE_SCALE_STEPS[name]) : base)
    };
  }
  return roles;
}
function designColors(colors, palette) {
  return { ...colors, ...paletteScalars(palette) };
}
function resolveDesignColor(value, colors) {
  const seen = /* @__PURE__ */ new Set();
  let current = value;
  while (current !== void 0 && !seen.has(current)) {
    if (!current.startsWith("#") && Object.hasOwn(colors, current)) {
      seen.add(current);
      current = colors[current];
    } else {
      return /^#?[0-9a-f]{6}$/i.test(current) ? current.replace(/^#/, "") : void 0;
    }
  }
  return void 0;
}
function validateDesignColors(system, colors) {
  const resolved = designColors(colors, system.palette);
  const entries = Object.entries(system.palette ?? {}).flatMap(
    ([key, value]) => Array.isArray(value) ? value.map((entry, i) => [`palette.${key}[${i}]`, entry]) : [[`palette.${key}`, value]]
  );
  for (const [name, role] of Object.entries(system.typography?.roles ?? {})) {
    if (role.color)
      entries.push([`typography.roles.${name}.color`, role.color]);
  }
  for (const [path, value] of entries) {
    if (value && !resolveDesignColor(value, resolved)) {
      throw new Error(
        `Unresolvable theme color at ${path}: "${value}" (unknown token or cycle)`
      );
    }
  }
}

export {
  TYPE_ROLES,
  TypeRoleNameSchema,
  CANVASES,
  TextCaseSchema,
  PaletteSchema,
  TypeRoleSchema,
  TypeRolesSchema,
  TypographySchema,
  DesignSpacingSchema,
  ChromeSchema,
  MotifSchema,
  DesignSystemProperties,
  DesignSystemSchema,
  designCanvas,
  ROLE_SCALE_STEPS,
  typeScaleSizes,
  capsFormatting,
  resolveMotif,
  resolveTypeRoles,
  designColors,
  resolveDesignColor,
  validateDesignColors
};
//# sourceMappingURL=chunk-JQGFMWOO.js.map